import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled, { withTheme } from 'styled-components'
import AdUnit from '../../../../../../components/AdUnit'
import { htmlParser, SMARTAD } from '../../../../../../modules/parse'
import Encryption from '../../../../../../modules/encryption'
import PlusDialog from './components/PlusDialog'
import GlobalBlurStyle from './components/GlobalBlurStyle'
import ErrorBoundary from '../../../../../Error/widgets/ErrorBoundary'
import translate from '../../../../../../modules/translate'
import consoleLogger from '../../../../../../modules/console-logger'
import TextSkeleton from '../../../../../../components/Text/views/TextSkeleton'
import isServerSide from '../../../../../../modules/is-server-side'
import { useUserContext } from '../../../../../../components/ContextUser'

const StyledAdUnit = styled(AdUnit)`
  float: right;
  margin-left: ${(props) => props.theme.sizes.gapVerticalHalf};
  margin-bottom: ${(props) => props.theme.sizes.gapVerticalHalf};
  @media (min-width: ${(props) => props.theme.tablet.breakPoint + 'px'}) {
    margin-right: -169px;
  }
`
const StyledMobileAdUnit = styled(AdUnit)`
  display: flex;
  width: calc(100% + ${(props) => props.theme.sizes.gapEdge} + ${(props) => props.theme.sizes.gapEdge});
  margin: 20px -${(props) => props.theme.sizes.gapEdge};
  justify-content: center;
  > div {
    margin-top: ${(props) => props.theme.mobile.sizes.gapVerticalHalf};
    text-align: center;
  }
`
const Wrapper = styled.div`
  position: relative;
  font-family: ${(props) => props.theme.font.primary};
  font-size: ${(props) => props.fontSize};
  color: rgba(0, 0, 0, 0.7);
  line-height: ${(props) => props.theme.lineHeight.text};

  iframe {
    max-width: 100%;
  }
`

const ContentWrapper = styled.div`
  position: relative;
  color: ${(props) => (props.inverted ? props.theme.color.textInverted : 'rgba(0, 0, 0, .7)')};
`

const PlusTeaser = styled(ContentWrapper)`
  margin-bottom: ${(props) => props.theme.mobile.sizes.gapVertical};
`

const NODECRYPT = 'no-decrypt'

const ContentDetailArticle = ({
  htmlContent,
  plus,
  plusTeaser,
  plusContent,
  embeds,
  adId,
  adTargeting,
  showAds,
  notTranslatedFontSize,
  fontSize,
  inverted,
  articleLayout,
  ...props
}) => {
  const [newFontSize, setNewFontSize] = useState(fontSize)
  const [consoleLog] = consoleLogger('ContentDetailArticle')
  const {
    access: { isPlus },
  } = useUserContext()
  consoleLog('plus', isPlus, 'UseAccess')
  const hideContent = plus ? !isPlus && plus : false

  useEffect(() => {
    setNewFontSize(fontSize)
  }, [fontSize])
  let themeMode = !isServerSide() && window.innerWidth > 599 ? { mobile: false } : { mobile: true }

  const content = ({ html, encodedContent, smartAd }) => {
    const parserOptions = {
      embeds,
      fontSize: notTranslatedFontSize,
      dropImageAttribs: true,
      articleLayout,
      adComponents: {
        desktop: !themeMode.mobile && <StyledAdUnit adId={adId} />,
        mobile: themeMode.mobile && <StyledMobileAdUnit adId={adId} />,
        showAds: showAds,
        smartAd: smartAd,
        targeting: adTargeting,
      },
    }

    if (encodedContent) {
      const encryption = new Encryption()

      if (hideContent) {
        parserOptions['adComponents']['smartAd'] = SMARTAD.disabled
        parserOptions['scrambleText'] = true
      }
      if (encodedContent !== NODECRYPT) {
        html = encryption.decrypt(encodedContent, process.env.RAZZLE_SPLUS_CONTENT_KEY)
      }
    }
    return htmlParser(html, parserOptions)
  }

  let encodedContent = plusContent
  if (hideContent && !plusContent) encodedContent = NODECRYPT

  const errorOptions = {
    component: translate('article'),
    link: '/',
    linkText: translate('error_try_homepage'),
    logError: 'Content Detail Article',
  }

  return (
    <ErrorBoundary {...errorOptions}>
      <Wrapper className="content" fontSize={newFontSize} {...props}>
        <GlobalBlurStyle />
        {(!plusContent || isPlus !== undefined) && (
          <>
            {hideContent && plusTeaser && (
              <PlusTeaser id="plusTeaser" inverted={inverted}>
                {content({ html: plusTeaser + '<br>', smartAd: SMARTAD.always })}
              </PlusTeaser>
            )}
            {hideContent && <PlusDialog id="plusDialog" />}
            <ContentWrapper id="articleContent" className={hideContent && 'blur'} inverted={inverted}>
              {content({ html: htmlContent, encodedContent: encodedContent })}
            </ContentWrapper>
          </>
        )}
        {plusContent && isPlus === undefined && (
          <>
            <TextSkeleton lines={5} fontSize="16px" lastWidth="45px" justify={false} />
            <TextSkeleton lines={3} fontSize="16px" lastWidth="65px" justify={false} />
          </>
        )}
      </Wrapper>
    </ErrorBoundary>
  )
}

ContentDetailArticle.propTypes = {
  htmlContent: PropTypes.string,
  plusTeaser: PropTypes.string,
  plusContent: PropTypes.string,
  embeds: PropTypes.object,
  adId: PropTypes.string,
  plus: PropTypes.bool,
}

ContentDetailArticle.defaultProps = {
  plusTeaser: null,
  plusContent: null,
}

export default withTheme(ContentDetailArticle)
